import Konva from 'konva';
import { InteriorObject } from './interior-object.model';
import { BaseDimensionComponent } from './components/commonComponents/BaseDimensionComponent';
import { NamingComponent } from './components/commonComponents/NamingComponent';
import { ImageComponent } from './components/commonComponents/ImageComponent';
import { BaseComponent } from './components/BaseComponent';
import * as _ from "underscore";
import { Position2D } from './other/position2D.model';
import { CloneService } from '../services/clone.service';
import { getComponentsWithout } from '../helper/getComponentsWithout';
import { getComponentFromSource } from '../generics-helper/getComponentFromSource.generic';
import { ComponentNames } from '../helper/componentNames';
import { mapDegreeToStep } from '../helper/mapDegreeToStep';
import { FloorComponent } from './components/containerComponents/FloorComponent';

export class EditorObject extends Konva.Image {
    components: Array<BaseComponent>;
    height3D: number;
    selfImage: HTMLImageElement;
    rotationStep: number;
    hasCollision: boolean;
    isSelected: boolean;
    private defaultImageName;

    constructor(interiorObject: InteriorObject) {
        //constructor overload
        if (interiorObject == null) {
            super(null);
            return;
        }

        const baseDim = getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, interiorObject.components);
        const path = '../../assets/editorItems/';
        const namingComp = getComponentFromSource<NamingComponent>(ComponentNames.namingComponent, interiorObject.components);
        const compName = namingComp ? namingComp.name : 'Name not defined';

        super({
            x: baseDim.posX,
            y: baseDim.posY,
            width: baseDim.box.width,
            height: baseDim.box.length,
            draggable: false,
            name: compName,
            image: null,
            rotation: baseDim.rotation,
            strokeWidth: 0
        });

        this.defaultImageName = 'empty.svg';
        this.components = getComponentsWithout(ComponentNames.baseDimensionsComponent, interiorObject.components);

        var imageObj = new Image();
        imageObj.src = path + this.defaultImageName;

        let floorComponent = getComponentFromSource<FloorComponent>(ComponentNames.floorComponent, interiorObject.components);

        if (floorComponent && floorComponent.floorImageBase64Data) {
            if (floorComponent.floorImageBase64Data !== '') {
                imageObj.src = floorComponent.floorImageBase64Data;

                if (floorComponent.isFloorImageVisible) {
                    this.show();
                }
                else {
                    this.hide();
                }
            }
        }
        else {
            let imageComponent = getComponentFromSource<ImageComponent>(ComponentNames.imageComponent, interiorObject.components);
            if (imageComponent !== null) {
                imageObj.src = path + imageComponent.srcName;
            }
        }

        this.selfImage = imageObj;
        this.rotationStep = mapDegreeToStep(baseDim.rotation);
        this.height3D = baseDim.box.height;
        this.hasCollision = false;
        this.isSelected = false;
    }

    changeBackgroundImage(url, layer) {
        let floorComponent = getComponentFromSource<FloorComponent>(ComponentNames.floorComponent, this.components);

        floorComponent.floorImageBase64Data = url;
        floorComponent.isFloorImageVisible = true;

        this.opacity(0.5);

        var imageObj = new Image();
        imageObj.src = url;
        this.selfImage = imageObj;
        this.image(this.selfImage);
        this.selfImage.onload = () => {
            layer.draw();
        }
    }

    toggleBackgroundImage() {
        let floorComponent = getComponentFromSource<FloorComponent>(ComponentNames.floorComponent, this.components);

        floorComponent.isFloorImageVisible = !floorComponent.isFloorImageVisible;
        if (floorComponent.isFloorImageVisible) {
            this.show();
        } else {
            this.hide();
        }
        const stage = this.getStage();
        
        if (stage) {
            stage.batchDraw();
        }
    }

    isBackgroundVisible() {
        return this.isVisible();
    }

    isClassVisible() {
        return this.fill() !== undefined;
    }

    toggleClassFill(color) {
        if (!this.isClassVisible()) {
            this.fill(color);
        }
        else {
            this.fill(undefined);
        }
        this.getStage().batchDraw();
    }

    isDefaultImage() {
        let result = this.selfImage.src.includes(this.defaultImageName);
        return result;
    }

    //redraws Layer as soon as Image is available, seems hacky
    loadImage(layer: Konva.Layer) {
        this.image(this.selfImage);
        this.selfImage.onload = () => { layer.draw() };
    }

    copy(): EditorObject {
        var cloneService = new CloneService();
        var nEo = new EditorObject(null);
        nEo.x(this.x());
        nEo.y(this.y());
        nEo.width(this.width());
        nEo.height(this.height());
        nEo.draggable(this.draggable());
        nEo.name(this.name());
        nEo.rotation(this.rotation());
        nEo.selfImage = this.selfImage;
        nEo.components = cloneService.deepClone(this.components);
        nEo.rotationStep = this.rotationStep;
        nEo.height3D = this.height3D;
        return nEo;
    }

    updateComponents(updatedComponents: Array<BaseComponent>) {
        this.components = getComponentsWithout(ComponentNames.baseDimensionsComponent, updatedComponents);
        var baseDim = getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, updatedComponents);
        this.x(baseDim.posX);
        this.y(baseDim.posY);
        this.width(baseDim.box.width);
        this.height(baseDim.box.length);
        this.height3D = baseDim.box.height;
    }

    getCenter2D(): Position2D {
        switch (this.rotationStep) {
            case 0:
                return new Position2D(this.x() + (this.width() / 2), this.y() + (this.height() / 2));
            case 1:
                return new Position2D(this.x() - (this.height() / 2), this.y() + (this.width() / 2));
            case 2:
                return new Position2D(this.x() - (this.width() / 2), this.y() - (this.height() / 2));
            case 3:
                return new Position2D(this.x() + (this.height() / 2), this.y() - (this.width() / 2));
        }
    }

    public rotateCustom(steps: number): void {
        if (steps != 1 && steps != -1)
            return;

        this.rotationStep += steps;
        if (this.rotationStep < 0)
            this.rotationStep += 4;

        this.rotationStep = this.rotationStep % 4;

        var tempRotation = 90 * this.rotationStep;

        const degToRad = Math.PI / 180

        const rotatePoint = ({ x, y }, deg) => {
            const rcos = Math.cos(deg * degToRad), rsin = Math.sin(deg * degToRad)
            return { x: x * rcos - y * rsin, y: y * rcos + x * rsin }
        }

        //current rotation origin (0, 0) relative to desired origin - center (this.width()/2, this.height()/2)
        const topLeftOffsetToCenter = { x: -this.width() / 2, y: -this.height() / 2 };
        const current = rotatePoint(topLeftOffsetToCenter, this.rotation());
        const rotated = rotatePoint(topLeftOffsetToCenter, tempRotation);
        const dx = rotated.x - current.x
        const dy = rotated.y - current.y;
        let newX = Math.round(this.x() + dx);
        let newY = Math.round(this.y() + dy);

        this.x(newX);
        this.y(newY);
        this.rotation(tempRotation);
    }
}